import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Button, Card, Chip, Spinner, Tag, TagGroup } from '@heroui/react'
import { api, ApiError } from '../../lib/api'
import { latestRead } from '../../lib/latestRead'
import { explain } from '../../lib/staffErrors'
import type { StaffKey } from '../../lib/staffText'
import type { ReportView } from '../../lib/types'
import { passUnsaved, useUnsaved } from '../../lib/unsaved'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { ActionMenu, Crumbs, Empty, Notice, PageHead, Pick, RangePick } from '../kit'
import { branchScope } from '../ticketReport'
import { builtIn, nameForCopy } from './builtins'
import { activeFilterCount, clearFilters, DATE_PRESETS, filterChips, hasFields, normaliseLayout, OFF_FIELDS, offeredFields, pruneLayout, removeExclude, sameLayout, setKeep } from './layout'
import { PivotTable } from './PivotTable'
import { SchedulesButton } from './PivotSchedules'
import { Rail } from './Rail'
import { LeaveDialog } from './ReportDialogs'
import { takeFresh } from './ReportsList'
import type { ReportsCtx } from './ReportsPage'
import type { DateFilter, PivotFields, PivotResult, PivotSpec } from './types'

/** After the first run every change re-runs this long after the last one. */
export const RERUN_DEBOUNCE_MS = 300
const BRANCH_KEY = 'qms-reports-branch'

let fieldsRead: Promise<PivotFields> | null = null
function readFields(): Promise<PivotFields> {
  fieldsRead ??= api.pivotFields().then(offeredFields, (err: unknown) => {
    fieldsRead = null
    throw err
  })
  return fieldsRead
}

function readBranch(): string {
  try {
    return sessionStorage.getItem(BRANCH_KEY) ?? ''
  } catch {
    return ''
  }
}

/** One report opened: a saved one by id, or a built-in by its id. */
export function Builder({ ctx, reportId, builtInId }: { ctx: ReportsCtx; reportId?: string; builtInId?: string }) {
  const { lang, say } = useStaffText()
  const [fields, setFields] = useState<PivotFields | null>(null)
  const [view, setView] = useState<ReportView | null>(null)
  const [error, setError] = useState<{ text: string; missing?: boolean } | null>(null)
  const bi = builtInId ? builtIn(builtInId) : undefined

  useEffect(() => {
    readFields().then(setFields, (err: unknown) => setError({ text: explain(err, lang, 'common.unreachable') }))
  }, [lang])
  useEffect(() => {
    if (!reportId) return
    api.reportView(reportId).then(
      (r) => setView(r.view),
      (err: unknown) => setError({ text: explain(err, lang, 'common.unreachable'), missing: err instanceof ApiError && err.status === 404 }),
    )
  }, [reportId, lang])

  const elsewhere = builtInId && (!bi || !bi.spec) ? (bi?.to ?? '/reports') : ''
  useEffect(() => {
    if (elsewhere) ctx.go(elsewhere)
  }, [elsewhere]) // eslint-disable-line react-hooks/exhaustive-deps
  if (elsewhere) return null
  if (error)
    return (
      <main className="page rp-page">
        <Crumbs onBack={() => ctx.go('/reports')} />
        <Card className="rp-box">{error.missing ? <Empty icon="table" title={say('pivot.gone')} /> : <Notice>{error.text}</Notice>}</Card>
      </main>
    )
  if (!fields || (reportId && !view) || !ctx.branches)
    return (
      <main className="page rp-page">
        <Card className="rp-box">
          <p className="rp-muted rp-loading">
            <Spinner size="sm" />
            {say('pivot.loading')}
          </p>
        </Card>
      </main>
    )
  const name = bi ? say(bi.name) : view!.name
  const spec = bi ? bi.spec! : view!.spec.pivot
  return <Work key={view?.id ?? bi!.id} ctx={ctx} fields={fields} view={view} name={name} spec={normaliseLayout(spec)} builtInName={bi ? name : undefined} onView={setView} />
}

function Work({
  ctx,
  fields,
  view,
  name,
  spec,
  builtInName,
  onView,
}: {
  ctx: ReportsCtx
  fields: PivotFields
  view: ReportView | null
  name: string
  spec: PivotSpec
  builtInName?: string
  onView: (v: ReportView) => void
}) {
  const { lang, say } = useStaffText()
  const known = useMemo(() => new Set([...fields.dimensions.map((d) => d.key), ...fields.measures.map((m) => m.key)]), [fields])
  const initial = useMemo(() => pruneLayout(spec, known), [spec, known])
  const [saved, setSaved] = useState<PivotSpec>(initial.layout)
  const [layout, setLayout] = useState<PivotSpec>(initial.layout)
  const own = !!view?.mine
  const [ran, setRan] = useState(() => !(view && takeFresh(view.id)) && hasFields(initial.layout))
  const [result, setResult] = useState<PivotResult | null>(null)
  const [runError, setRunError] = useState('')
  const [running, setRunning] = useState(false)
  const [runAt, setRunAt] = useState(0)
  const [rerun, setRerun] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [leaving, setLeaving] = useState(false)
  const [saving, setSaving] = useState(false)
  const branches = useMemo(() => ctx.branches ?? [], [ctx.branches])
  const [branch, setBranchState] = useState(() => {
    const b = readBranch()
    return branches.some((x) => x.id === b) ? b : ''
  })
  const setBranch = (b: string) => {
    setBranchState(b)
    try {
      sessionStorage.setItem(BRANCH_KEY, b)
    } catch {
      // Kept for this visit only.
    }
  }
  const branchIds = useMemo(() => (branch ? [branch] : branchScope(branches.map((b) => b.id), branches)), [branch, branches])

  useEffect(() => {
    for (const key of initial.dropped) if (!OFF_FIELDS.has(key)) ctx.toast(say('pivot.fieldGone', { field: key }), 'error')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const draft = own && !sameLayout(layout, saved)
  useUnsaved(draft)
  useEffect(() => {
    if (!draft) return
    const before = (e: BeforeUnloadEvent) => e.preventDefault()
    addEventListener('beforeunload', before)
    return () => removeEventListener('beforeunload', before)
  }, [draft])

  // The run: after the first, every change re-runs RERUN_DEBOUNCE_MS after the last one.
  const runKey = JSON.stringify([layout, branchIds, rerun])
  useEffect(() => {
    if (!ran || !hasFields(layout) || branchIds.length === 0) return
    let stop: (() => void) | undefined
    const timer = setTimeout(() => {
      setRunning(true)
      stop = latestRead((signal) => api.pivotRun(branchIds, layout, signal), {
        done: (r) => {
          setResult(r)
          setRunError('')
          setRunAt(Date.now())
        },
        failed: (err) => setRunError(runProblem(err, lang, say)),
        settled: () => setRunning(false),
      })
    }, RERUN_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      stop?.()
    }
  }, [ran, runKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!runAt) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [runAt])

  const label = useCallback((key: string) => fields.dimensions.find((d) => d.key === key)?.label ?? fields.measures.find((m) => m.key === key)?.label ?? key, [fields])
  const kind = useCallback((key: string) => fields.measures.find((m) => m.key === key)?.kind ?? 'count', [fields])
  const values = useCallback((field: string, signal: AbortSignal) => api.pivotValues(branchIds, layout, field, signal).then((r) => r.values), [branchIds, layout])

  const back = () => (draft ? setLeaving(true) : ctx.go('/reports'))

  const save = async () => {
    if (!view) return
    setSaving(true)
    try {
      const r = await api.updateReportView(view.id, { name: view.name, shared: view.shared, description: view.description ?? '', spec: { period: '', pivot: layout }, updatedAt: view.updatedAt })
      setSaved(layout)
      onView(r.view)
      ctx.toast(say('pivot.saved'))
    } catch (err) {
      ctx.toast(explain(err, lang, 'common.unreachable'), 'error')
    } finally {
      setSaving(false)
    }
  }
  const duplicate = async () => {
    setSaving(true)
    try {
      const r = view
        ? await api.duplicateReportView(view.id, { period: '', pivot: layout })
        : await api.saveReportView({ name: await nameForCopy(builtInName ?? name, say, ctx.organisation), kind: 'pivot', shared: true, spec: { period: '', pivot: layout }, organisation: ctx.organisation })
      ctx.toast(say('pivot.duplicated'))
      ctx.go(`/reports/${r.view.id}`)
    } catch (err) {
      ctx.toast(explain(err, lang, 'common.unreachable'), 'error')
      setSaving(false)
    }
  }

  const seconds = Math.max(1, Math.round((now - runAt) / 1000))
  const meta: ReactNode =
    ran && result ? (
      <>
        {say('pivot.lastRun')} <b>{say('pivot.secondsAgo', { n: seconds })}</b> · {say('pivot.scanned', { n: result.scanned.toLocaleString('en-GB') })}
      </>
    ) : own ? (
      say('pivot.metaHint')
    ) : null

  const chips = filterChips(layout, label)
  const nFilters = activeFilterCount(layout)
  const owner = view ? (view.mine ? say('pivot.you') : view.ownerName || say('pivot.formerStaff')) : ''
  // A view-only report with nothing placed has no layout to show.
  const railless = !own && !hasFields(layout)

  type FilterChip = ReturnType<typeof filterChips>[number]
  const chipText = (c: FilterChip) =>
    c.kind === 'keep' ? `${c.label}: ${c.values!.length > 2 ? say('pivot.nValues', { n: c.values!.length }) : c.values!.join(', ')}` : say('pivot.notValue', { value: c.value! })
  const chipId = (c: FilterChip) => `${c.kind}\u0001${c.key}\u0001${c.value ?? ''}`
  const filtersLabel = say(nFilters === 1 ? 'pivot.oneFilter' : 'pivot.nFilters', { n: nFilters })

  return (
    <main className="page rp-page rp-page--builder">
      <PageHead
        crumbs={<Crumbs name={name} onBack={back} />}
        title={name}
        extra={
          <>
            {draft && (
              <Chip size="sm" variant="soft" color="warning">
                {say('pivot.draft')}
              </Chip>
            )}
            {!own && (
              <Chip size="sm" variant="soft">
                {view ? say('pivot.viewOnlyBy', { owner }) : say('pivot.builtInViewOnly')}
              </Chip>
            )}
          </>
        }
        line={meta ? <span className="rp-meta">{meta}</span> : undefined}
        actions={
          <>
            <Pick
              className="rp-branch"
              label={say('pivot.branch')}
              value={branch}
              onChange={setBranch}
              options={[{ value: '', label: say('pivot.allBranches') }, ...branches.map((b) => ({ value: b.id, label: b.name }))]}
            />
            <ExportMenu disabled={!ran || !result} branchIds={branchIds} layout={layout} title={name} toast={ctx.toast} />
            {view && <SchedulesButton reportId={view.id} branches={branches} toast={ctx.toast} />}
            {own ? (
              <Button variant={draft ? 'primary' : 'secondary'} size="sm" isDisabled={saving || !draft} onPress={() => void save()}>
                <Icon name="check" size={16} />
                {say(draft ? 'pivot.saveChanges' : 'pivot.savedState')}
              </Button>
            ) : (
              <Button variant="primary" size="sm" isDisabled={saving} onPress={() => void duplicate()}>
                <Icon name="copy" size={16} />
                {say('pivot.duplicateToEdit')}
              </Button>
            )}
          </>
        }
      />

      <Card className="rp-builder" data-norail={railless ? '' : undefined}>
        {!railless && <Rail fields={fields} layout={layout} onChange={setLayout} values={values} onRefused={(key: StaffKey) => ctx.toast(say(key), 'error')} readOnly={!own} />}
        <div className="rp-main">
          <div className="rp-tools">
            <DatePick date={layout.date} onChange={(date) => setLayout({ ...layout, date })} />
            <span className="rp-grow" />
            {running && (
              <span className="rp-muted rp-running">
                <Spinner size="sm" />
                {say('pivot.running')}
              </span>
            )}
            {(own || hasFields(layout)) && (
              <Button variant="secondary" size="sm" isDisabled={!hasFields(layout)} onPress={() => (ran ? setRerun((n) => n + 1) : setRan(true))}>
                <Icon name="play" size={14} />
                {say('pivot.run')}
              </Button>
            )}
          </div>
          {nFilters > 0 && (
            <div className="rp-filters">
              <Icon name="filter" size={14} />
              <b>{filtersLabel}</b>
              <TagGroup
                aria-label={filtersLabel}
                size="sm"
                className="rp-tags"
                onRemove={
                  own
                    ? (keys) => {
                        let next = layout
                        for (const c of chips) {
                          if (!keys.has(chipId(c))) continue
                          next = c.kind === 'keep' ? setKeep(next, c.key, []) : removeExclude(next, c.key, c.value ?? '')
                        }
                        setLayout(next)
                      }
                    : undefined
                }
              >
                <TagGroup.List>
                  {chips.map((c) => {
                    const text = chipText(c)
                    return (
                      <Tag key={chipId(c)} id={chipId(c)} textValue={text}>
                        {text}
                        {own && <Tag.RemoveButton aria-label={say('pivot.removeFilter', { filter: text })} />}
                      </Tag>
                    )
                  })}
                </TagGroup.List>
              </TagGroup>
              {own && (
                <Button variant="ghost" size="sm" className="rp-filters__clear" onPress={() => setLayout(clearFilters(layout))}>
                  {say('pivot.clear')}
                </Button>
              )}
            </div>
          )}
          <div className="rp-canvas">
            {!hasFields(layout) ? (
              // The line under the heading already says what to do; the empty canvas only names its state.
              <Empty icon="table" title={say(own ? 'pivot.noData' : 'pivot.emptyReport')} />
            ) : !ran ? (
              <Empty
                icon="play"
                title={say('pivot.readyToRun')}
                action={
                  <Button variant="primary" size="sm" onPress={() => setRan(true)}>
                    <Icon name="play" size={14} />
                    {say('pivot.run')}
                  </Button>
                }
              />
            ) : runError && !running ? (
              <div className="rp-canvas__error">
                <Notice>{runError}</Notice>
                <Button variant="secondary" size="sm" onPress={() => setRerun((n) => n + 1)}>
                  {say('pivot.tryAgain')}
                </Button>
              </div>
            ) : !result && branchIds.length > 0 ? (
              <Empty title={say('pivot.running')} />
            ) : !result || result.scanned === 0 ? (
              <Empty icon="search" title={say('pivot.noRows')}>
                {say('pivot.noRowsHint')}
              </Empty>
            ) : (
              <div className="rp-result" data-stale={running ? '' : undefined}>
                <PivotTable result={result!} layout={layout} onChange={setLayout} label={label} kind={kind} readOnly={!own} />
              </div>
            )}
          </div>
        </div>
      </Card>

      {leaving && (
        <LeaveDialog
          onStay={() => setLeaving(false)}
          onLeave={() => {
            passUnsaved()
            ctx.go('/reports')
          }}
        />
      )}
    </main>
  )
}

function runProblem(err: unknown, lang: Parameters<typeof explain>[1], say: (k: StaffKey) => string): string {
  if (err instanceof ApiError) {
    if (err.reason === 'PIVOT_TOO_WIDE' || err.code === 'PIVOT_TOO_WIDE') return say('pivot.tooWide')
    if (err.reason === 'PIVOT_TOO_MUCH' || err.code === 'PIVOT_TOO_MUCH') return say('pivot.tooMuch')
  }
  return explain(err, lang, 'common.unreachable')
}

/** The date range: a preset, or Custom with a from–to field beside it. */
function DatePick({ date, onChange }: { date: DateFilter; onChange: (d: DateFilter) => void }) {
  const { say } = useStaffText()
  const [custom, setCustom] = useState(date.preset === 'custom')
  const preset = custom || date.preset === 'custom' ? 'custom' : DATE_PRESETS.some((p) => p.value === date.preset) ? date.preset : 'last_30'
  return (
    <span className="rp-datepick">
      <Pick
        label={say('pivot.dateRange')}
        value={preset}
        onChange={(v) => {
          if (v === 'custom') return setCustom(true)
          setCustom(false)
          onChange({ preset: v as Exclude<DateFilter['preset'], 'custom'> })
        }}
        options={[...DATE_PRESETS.map((p) => ({ value: p.value, label: say(p.key) })), { value: 'custom', label: say('pivot.custom') }]}
      />
      {preset === 'custom' && (
        <RangePick
          label={say('pivot.dateRange')}
          from={date.preset === 'custom' ? (date.from ?? '') : ''}
          to={date.preset === 'custom' ? (date.to ?? '') : ''}
          onChange={(from, to) => onChange({ preset: 'custom', from, to })}
        />
      )}
    </span>
  )
}

function ExportMenu({ disabled, branchIds, layout, title, toast }: { disabled: boolean; branchIds: string[]; layout: PivotSpec; title: string; toast: (t: string) => void }) {
  const { say } = useStaffText()
  const download = (kind: 'xlsx' | 'csv') => {
    toast(say('pivot.exporting'))
    const a = document.createElement('a')
    a.href = api.pivotFile(branchIds, layout, title, kind)
    a.rel = 'noopener'
    document.body.append(a)
    a.click()
    a.remove()
  }
  return (
    <ActionMenu
      label={say('pivot.export')}
      text={say('pivot.export')}
      icon="download"
      isDisabled={disabled}
      items={[
        { id: 'xlsx', label: say('pivot.excel'), icon: 'table', run: () => download('xlsx') },
        { id: 'csv', label: say('pivot.csv'), icon: 'document', run: () => download('csv') },
      ]}
    />
  )
}
